import {
  Card,
  Container,
  Flex,
  Heading,
  Loader,
  Link as IrisLink,
  Paragraph,
  Stack,
  Box,
  EmptyStateList,
  EmptyState,
} from "@eworkit/iris";
import React from "react";
import { FC } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { Link as RouterLink } from "react-router-dom";

import { fetchTasks } from "@/data/tasks";
import { router } from "@/views/router";

import { DashboardDCCTask, DashboardWOCTask } from "../components";

interface DashboardTasksSectionProps {
  name?: string;
  companyId: number;
}

export const DashboardTasksSection: FC<DashboardTasksSectionProps> = ({ name, companyId }) => {
  const { t } = useTranslation("session");

  const { data, isFetching } = useQuery(["dashboard-tasks", companyId], () => fetchTasks({ size: 2, companyId }), {
    suspense: false,
  });

  return (
    <Container as={Card} sx={{ gridColumn: ["1 / 5", null, null, "3 / 5"] }}>
      <Container sx={{ height: "100%", display: "flex", flexDirection: "column", pt: "xs" }}>
        <Heading as={Box} pb="xxs" size={["h-md", null, null, "h-lg"]}>
          {t("DASHBOARD.TASKS_SECTION.HEADER", { name })}
        </Heading>

        {isFetching ? (
          <Flex flex={1} alignItems="center" justifyContent="center">
            <Loader size="xl" />
          </Flex>
        ) : data?.totalElements == 0 ? (
          <Flex flex={1} alignItems="center" justifyContent="center">
            <Box py="lg">
              <EmptyState
                icon={<EmptyStateList />}
                title={t("TASKS.NO_TASKS")}
                content={t("TASKS.NO_TASKS_DESCRIPTION")}
              />
            </Box>
          </Flex>
        ) : (
          <React.Fragment>
            <Paragraph size={["p-sm", null, null, "p-md"]}>
              <Trans
                ns="session"
                i18nKey={
                  data?.totalElements === 1
                    ? "DASHBOARD.TASKS_SECTION.SUBHEADER"
                    : "DASHBOARD.TASKS_SECTION.SUBHEADER_PLURAL"
                }
                values={{
                  count: data?.totalElements,
                }}>
                <Paragraph bold></Paragraph>
              </Trans>
            </Paragraph>
            <Flex as={Box} py="sm" flex={1} alignItems="start" justifyContent="center">
              <Stack as={Container} gap="sm">
                {data &&
                  data.content.map(task =>
                    task.type === "WO_CONFIRM" ? (
                      <DashboardWOCTask key={task.id} task={task} />
                    ) : (
                      <DashboardDCCTask key={task.id} task={task} />
                    ),
                  )}
              </Stack>
            </Flex>
            <IrisLink as={RouterLink} to={router.get("tasks").path}>
              <Paragraph size="p-md">{t("DASHBOARD.TASKS_SECTION.VIEW_ALL")}</Paragraph>
            </IrisLink>
          </React.Fragment>
        )}
      </Container>
    </Container>
  );
};
